import { all, call, put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import { Awaited } from 'ts-essentials';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import workspaceService from '@app/libs/workspaceService';
import {
  EDITING_DOCUMENTS_FILE_DOWNLOAD_REQUEST,
  EDITING_DOCUMENTS_LIST_DELETE_FAILURE,
  EDITING_DOCUMENTS_LIST_DELETE_REQUEST,
  EDITING_DOCUMENTS_LIST_DELETE_SUCCESS,
  EDITING_DOCUMENTS_LIST_FETCH_FAILURE,
  EDITING_DOCUMENTS_LIST_FETCH_REQUEST,
  EDITING_DOCUMENTS_LIST_FETCH_SUCCESS,
} from '@app/store/actionTypes';
import {
  editingDocumentsFileDownloadFailure,
  editingDocumentsFileDownloadRequest,
  editingDocumentsFileDownloadSuccess,
  editingDocumentsListDeleteFailure,
  editingDocumentsListDeleteRequest,
  editingDocumentsListDeleteSuccess,
  editingDocumentsListFetchFailure,
  editingDocumentsListFetchRequest,
  editingDocumentsListFetchSuccess,
} from '@app/store/editingDocuments/actions';
import { getAllFilters, getLimit } from '@app/store/editingDocuments/selectors';
import { modalOpen } from '@app/store/modal/actions';
import {
  deleteEditingDocuments as deleteEditingDocumentsRequest,
  getEditingDocuments,
} from '@app/store/transport-layer/document/editing-documents';
import { getEditingDocumentsFile } from '@app/store/transport-layer/document/editing-documents/file';
import { downloadFileFromURLSaga } from '@app/store/transport-layer/files';

export function* deleteEditingDocuments({
  payload: editingDocumentIds,
}: ReturnType<typeof editingDocumentsListDeleteRequest>) {
  try {
    yield call(deleteEditingDocumentsRequest, workspaceService.getWorkspaceId(), {
      editingDocumentIds,
    });
    yield put(editingDocumentsListDeleteSuccess());
  } catch (error) {
    yield put(editingDocumentsListDeleteFailure(error));
  }
}

export function* reloadEditingDocumentsList() {
  const nowFilters: ReturnType<typeof getAllFilters> = yield select(getAllFilters);
  yield put(editingDocumentsListFetchRequest(nowFilters));
}

export function* loadEditingDocumentsList({
  payload: { limit, offset, searchKeyword, memberId },
}: ReturnType<typeof editingDocumentsListFetchRequest>) {
  try {
    const data: Awaited<ReturnType<typeof getEditingDocuments>> | undefined = yield call(
      getEditingDocuments,
      workspaceService.getWorkspaceId(),
      {
        limit,
        offset,
        searchKeyword,
        memberId,
      }
    );
    yield put(
      editingDocumentsListFetchSuccess({
        limit,
        editingDocuments: data?.editingDocuments ?? [],
        maxPage: Math.ceil((data?.count ?? 0) / limit) || 1,
        offset,
        searchKeyword,
        totalCount: data?.count ?? 0,
        memberId,
      })
    );
  } catch (error) {
    yield put(editingDocumentsListFetchFailure(error));
  }
}

export function* reloadEditingDocumentsListForPageOver({
  payload: { offset, maxPage },
}: ReturnType<typeof editingDocumentsListFetchSuccess>) {
  const limit: ReturnType<typeof getLimit> = yield select(getLimit);
  const page = Math.ceil((offset + 1) / limit);
  if (page > maxPage) {
    const nowFilters: ReturnType<typeof getAllFilters> = yield select(getAllFilters);
    yield put(editingDocumentsListFetchRequest({ ...nowFilters, offset: (maxPage - 1) * limit }));
  }
}

export function* notifyDeleteEditingDocumentsFailure({
  payload: { message },
}: ReturnType<typeof editingDocumentsListDeleteFailure>) {
  yield put(
    modalOpen('CONFIRM', {
      title: '편집 문서 삭제 실패',
      content: GLOBAL_ERROR_MESSAGE[message] || message,
    })
  );
}

export function* notifyEditingDocumentsListFetchFailure({
  payload: { message },
}: ReturnType<typeof editingDocumentsListFetchFailure>) {
  yield put(
    modalOpen('CONFIRM', {
      title: '편집 문서 불러오기 실패',
      content: GLOBAL_ERROR_MESSAGE[message] || message,
    })
  );
}

export function* downloadEditingDocumentFile({
  payload: { editingDocumentId, title },
}: ReturnType<typeof editingDocumentsFileDownloadRequest>) {
  try {
    const url: string = yield call(
      getEditingDocumentsFile,
      workspaceService.getWorkspaceId(),
      editingDocumentId
    );
    yield call(downloadFileFromURLSaga, {
      url,
      saveFileName: `${title}.pdf`,
    });
    yield put(editingDocumentsFileDownloadSuccess());
  } catch (error) {
    yield put(editingDocumentsFileDownloadFailure());
    yield put(
      modalOpen('CONFIRM', {
        title: '문서 파일 다운로드 실패',
        content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
      })
    );
  }
}

export default function* editingDocumentsSagas() {
  yield all([
    takeLeading(EDITING_DOCUMENTS_LIST_DELETE_REQUEST, deleteEditingDocuments),
    takeLeading(EDITING_DOCUMENTS_LIST_DELETE_SUCCESS, reloadEditingDocumentsList),
    takeLatest(EDITING_DOCUMENTS_LIST_FETCH_REQUEST, loadEditingDocumentsList),
    takeLatest(EDITING_DOCUMENTS_LIST_FETCH_SUCCESS, reloadEditingDocumentsListForPageOver),
    takeLatest(EDITING_DOCUMENTS_LIST_DELETE_FAILURE, notifyDeleteEditingDocumentsFailure),
    takeLatest(EDITING_DOCUMENTS_LIST_FETCH_FAILURE, notifyEditingDocumentsListFetchFailure),
    takeLatest(EDITING_DOCUMENTS_FILE_DOWNLOAD_REQUEST, downloadEditingDocumentFile),
  ]);
}
